import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", export_rep: "", contact_email: "", contact_person: "", address: "",
  destination_port: "", payment_method: "", currency: "USD", price_coefficient: "1",
  sea_freight_per_container: "", notes: "", sales_rep_id: "",
};

export default function CustomerFormDialog({ open, onOpenChange, customer, onSubmit }) {
  const [form, setForm] = useState(EMPTY);
  // iter 178j: load sales_rep + admin users so we can populate the
  // assignment dropdown. Filtered client-side because /users/searchable
  // returns all roles.
  const [salesReps, setSalesReps] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/users/searchable`, { withCredentials: true })
      .then((r) => setSalesReps((r.data || []).filter((u) => u.role === "sales_rep" || u.role === "admin")))
      .catch(() => setSalesReps([]));
  }, [open]);

  useEffect(() => {
    if (customer) {
      setForm({
        name: customer.name || "",
        export_rep: customer.export_rep || "",
        contact_email: customer.contact_email || "",
        contact_person: customer.contact_person || "",
        address: customer.address || "",
        destination_port: customer.destination_port || "",
        payment_method: customer.payment_method || "",
        currency: customer.currency || "USD",
        price_coefficient: customer.price_coefficient ?? 1,
        sea_freight_per_container: customer.sea_freight_per_container ?? "",
        notes: customer.notes || "",
        sales_rep_id: customer.sales_rep_id || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [customer, open]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      price_coefficient: form.price_coefficient === "" ? 1 : parseFloat(form.price_coefficient),
      sea_freight_per_container: form.sea_freight_per_container === "" ? null : parseFloat(form.sea_freight_per_container),
      sales_rep_id: form.sales_rep_id || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight text-gray-900">
            {customer ? "Edit Customer" : "Add Customer"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-xs font-medium text-gray-600">Customer Name *</Label>
            <Input
              data-testid="customer-form-name"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Export Rep</Label>
            <Input
              data-testid="customer-form-export-rep"
              value={form.export_rep}
              onChange={(e) => update("export_rep", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Assigned Sales Rep</Label>
            <Select value={form.sales_rep_id || "unset"} onValueChange={(v) => update("sales_rep_id", v === "unset" ? "" : v)}>
              <SelectTrigger data-testid="customer-form-sales-rep" className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="rounded-lg">
                <SelectItem value="unset">— Unassigned —</SelectItem>
                {salesReps.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email} <span className="text-gray-400 text-xs">({u.role === "admin" ? "Admin" : "Sales Rep"})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Contact Email</Label>
            <Input
              data-testid="customer-form-contact-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => update("contact_email", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Contact Person</Label>
            <Input
              data-testid="customer-form-contact-person"
              value={form.contact_person}
              onChange={(e) => update("contact_person", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Destination Port</Label>
            <Input
              data-testid="customer-form-destination-port"
              value={form.destination_port}
              onChange={(e) => update("destination_port", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-medium text-gray-600">Address</Label>
            <Input
              data-testid="customer-form-address"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Payment Method</Label>
            <Input
              data-testid="customer-form-payment-method"
              value={form.payment_method}
              onChange={(e) => update("payment_method", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
              <SelectTrigger data-testid="customer-form-currency" className="mt-1.5 rounded-lg border-gray-300 focus:ring-brand-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-lg">
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="CNY">CNY</SelectItem>
                <SelectItem value="CAD">CAD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Price Coefficient</Label>
            <Input
              data-testid="customer-form-price-coefficient"
              type="number"
              step="0.01"
              value={form.price_coefficient}
              onChange={(e) => update("price_coefficient", e.target.value)}
              placeholder="1.00"
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-gray-600">Sea Freight (40', USD)</Label>
            <Input
              data-testid="customer-form-sea-freight"
              type="number"
              step="0.01"
              value={form.sea_freight_per_container}
              onChange={(e) => update("sea_freight_per_container", e.target.value)}
              placeholder="Leave blank to use global default"
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-medium text-gray-600">Notes</Label>
            <Input
              data-testid="customer-form-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
            />
          </div>
          <DialogFooter className="sm:col-span-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg border-gray-300">Cancel</Button>
            <Button type="submit" data-testid="customer-form-submit" className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors">
              {customer ? "Save Changes" : "Create Customer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
